import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Collapse,
} from '@mui/material';
import { green } from '@mui/material/colors';
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Call as CallIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../Redux/store';
import { clearOrderConfirmationModal } from '../../Redux/Slices/notificationSlice';
import { notificationService } from '../../service/FCM/NotificationService';
import { viewOrderDetails, initiateExotelCall } from '../../service/order/orderService';
import { useAppSelector } from '../../Redux/hooks';
import { notifyError } from '../ToastContainer/Toast/Toast';
import CallInitiatedModal from '../CallInitiatedModal/CallInitiatedModal';
import CancelOrderModal from './CancelOrderModal';
import AcceptOrderModal from './AcceptOrderModal';
import nonVegIcon from '../../Assets/Non_veg_symbol.svg.png';
import vegIcon from '../../Assets/Veg_symbol.svg.png';
import eggIcon from '../../Assets/Egg.svg';

interface OrderData {
  id: number;
  order_code: string;
  order_date: string;
  status: string;
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  billing_address: {
    city: string;
    name: string;
    email: string;
    phone: string;
    state: string;
    pincode: string;
    address1: string;
    address2: string;
    address3: string;
  };
  shipping_address: {
    city: string;
    name: string;
    email: string;
    phone: string;
    state: string;
    pincode: string;
    address1: string;
    address2: string;
    address3: string;
  };
  store_data: {
    logo: string;
    name: string;
    gst_no: string;
    fssai_no: string;
    gst_inclusive: boolean;
  };
  order_through: string;
  delivery_type: string;
  payment_status: string;
  payment_method: string;
  preparation_time: string;
  external_order_id: string;
  instruction?: string;
  tax_amount: string;
  discount_amount: string;
  sub_total: string;
  shipping_charge: string;
  cgst: string;
  sgst: string;
  igst: string;
  total_amount: string;
  order_items: Array<{
    id: number;
    product_id: string;
    product_image: string | null;
    product_name: string;
    product_type: string;
    quantity: number;
    original_price: string;
    price: string;
    discount_type: string;
    discount_value: string;
    gst_price: string;
    is_gst_inclusive: boolean;
    gst_percentage: string;
    total_price: string;
    billed_quantity: number;
    cancelled_quantity: number;
    returned_quantity: number;
    return_window: number;
    is_returnable: boolean;
    is_cancellable: boolean;
  }>;
}

const OrderConfirmationModal: React.FC = () => {
  const dispatch = useDispatch();
  // ✅ Add safe optional chaining and default fallback to prevent undefined errors
  const orderConfirmationModal = useSelector((state: RootState) => state.notification?.orderConfirmationModal) || {
    visible: false,
    orderData: null,
    confirmationType: null,
    title: '',
    body: '',
  };

  // State management
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [paymentSummaryExpanded, setPaymentSummaryExpanded] = useState(false);
  const [calling, setCalling] = useState(false);
  const [showCallInitiatedModal, setShowCallInitiatedModal] = useState(false);
  // State for live-updating time
  const [timeAgo, setTimeAgo] = useState<string>('Just now');

  // Get profile phone_number from Redux
  const profilePhoneNumber = useAppSelector((state) => state.roleAndPermission?.phone_number || '');

  // Live-updating time ago function
  const getTimeAgo = (dateString: string): string => {
    if (!dateString) return 'Just now';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 1) {
      return 'Just now';
    } else if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `more than ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(seconds / 86400);
      return `more than ${days} day${days !== 1 ? 's' : ''}`;
    }
  };

  // Get display data (needed for useEffect dependency)
  const displayData = orderData || orderConfirmationModal.orderData;

  // Update time every second when modal is visible
  useEffect(() => {
    if (!orderConfirmationModal.visible || !displayData?.order_date) {
      setTimeAgo('Just now');
      return;
    }

    // Update immediately
    setTimeAgo(getTimeAgo(displayData.order_date));

    // Update every second
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo(displayData.order_date));
    }, 1000);

    return () => clearInterval(interval);
  }, [orderConfirmationModal.visible, displayData?.order_date]);

  // Fetch full order details from API when modal opens
  // FCM notification data may not include order_items, customer info, etc.
  // Without full data, Accept/Cancel will fail with "Failed to process the order"
  useEffect(() => {
    if (!orderConfirmationModal.visible || !orderConfirmationModal.orderData) return;

    const orderId = orderConfirmationModal.orderData.id ?? orderConfirmationModal.orderData.orderId;
    if (!orderId) return;

    let cancelled = false;
    const fetchFullOrderData = async () => {
      try {
        setLoading(true);
        const res = await viewOrderDetails(orderId, false);
        const data = res?.data?.data;
        if (!cancelled && data) {
          setOrderData(data);
          console.log('[OrderConfirmationModal] Full order details fetched for order:', orderId);
        }
      } catch (error) {
        console.warn('[OrderConfirmationModal] Failed to fetch order details:', error);
        // Continue with FCM data as fallback — modal will still show
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFullOrderData();
    return () => { cancelled = true; };
  }, [orderConfirmationModal.visible, orderConfirmationModal.orderData]);

  // Try to initialize audio when popup appears
  useEffect(() => {
    if (!orderConfirmationModal.visible || !orderConfirmationModal.orderData) return;

    const tryInitializeAudio = async () => {
      try {
        const audioStatus = notificationService.getAudioStatus?.();
        if (!audioStatus?.ready) {
          await notificationService.tryInitializeAudio?.();
        }
      } catch (error) {
        console.warn('[OrderConfirmationModal] Error trying to initialize audio:', error);
      }
    };

    tryInitializeAudio();
  }, [orderConfirmationModal.visible]);

  // Multi-device sync: poll order status when modal is open so popup closes if order was accepted/cancelled on another device
  // IMPORTANT: Stop polling when Accept/Cancel sub-modal is open to prevent race condition:
  // polling detects status change → navigates → abortInFlightRequests() aborts the in-flight accept/cancel API call
  useEffect(() => {
    if (!orderConfirmationModal.visible || !orderConfirmationModal.orderData) return;
    // Pause polling while user is processing an order (Accept/Cancel modal open)
    if (showAcceptModal || showCancelModal) return;

    const orderId = orderConfirmationModal.orderData.id ?? orderConfirmationModal.orderData.orderId;
    if (!orderId) return;

    const POLL_INTERVAL_MS = 8000;

    const checkOrderStatus = async () => {
      try {
        const res = await viewOrderDetails(orderId, false);
        const data = res?.data?.data;
        const status = typeof data?.status === 'string' ? data.status.toLowerCase() : '';
        // Remove from queue if order is no longer pending (accepted/billed/cancelled/preparing etc.)
        // This enables cross-browser sync: when order is accepted in Chrome, Firefox detects it here
        if (status && status !== 'pending') {
          console.log('[OrderConfirmationModal] Order status changed to:', status, '- removing from queue');
          notificationService.removeOrderFromQueue(orderId);
        }
      } catch {
        // Ignore errors (e.g. network); will retry next poll
      }
    };

    const intervalId = setInterval(checkOrderStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [orderConfirmationModal.visible, orderConfirmationModal.orderData, showAcceptModal, showCancelModal]);

  const handleAccept = () => {
    setShowAcceptModal(true);
  };

  const handleReject = () => {
    setShowCancelModal(true);
  };

  const handleAcceptSuccess = () => {
    // Success handling is now done in the service
    console.log('Accept success callback triggered');
  };

  const handleCancelSuccess = () => {
    // Success handling is now done in the service
    console.log('Cancel success callback triggered');
  };

  const handleClose = () => {
    // Stop continuous sound and navigate
    notificationService.closeOrderConfirmationModal();
  };

  // Reset fetched order data when modal closes so stale data doesn't persist for next order
  useEffect(() => {
    if (!orderConfirmationModal.visible) {
      setOrderData(null);
    }
  }, [orderConfirmationModal.visible]);

  if (!orderConfirmationModal.visible || !orderConfirmationModal.orderData) {
    return null;
  }

  const queueStatus = notificationService.getQueueStatus();
  const additionalOrders = Math.max(0, (queueStatus.length || 0) - 1);
  const waitingMessage =
    additionalOrders > 0
      ? `+${additionalOrders} more order${additionalOrders > 1 ? 's' : ''} waiting`
      : null;

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getProductTypeIcon = (productType: string) => {
    return productType === 'non-veg' ?
      <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16 }} />
      : productType === 'veg' ? <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16 }} />
        : productType === 'egg' ? <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16 }} /> : <></>;
  };

  // Get status color
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'billed':
      case 'delivered':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  const mainItems = displayData.order_items.filter(item => !item.product_id.startsWith('a-'));
  // Paid when payment is actually received (incl. a prepaid/collected COD order),
  // not only when the method is 'online'. A COD order can be paid upfront via UPI,
  // in which case payment_status becomes 'received' while payment_method stays 'cod'.
  const paymentStatusLabel =
    displayData.payment_status === 'received' ||
    displayData.payment_status === 'paid' ||
    displayData.payment_method === 'online'
      ? 'Paid'
      : 'Not Paid';
  const isPaymentPaid = paymentStatusLabel === 'Paid';

  // ✅ ENHANCED: Handle click on modal to initialize audio (user interaction)
  const handleModalClick = () => {
    // Try to initialize audio when user clicks anywhere on the modal
    if (notificationService && !notificationService.isAudioReady?.()) {
      console.log('[OrderConfirmationModal] User clicked modal, attempting to initialize audio...');
      notificationService.tryInitializeAudio?.().then(() => {
        const audioStatus = notificationService.getAudioStatus?.();
        if (audioStatus?.ready) {
          console.log('[OrderConfirmationModal] ✅ Audio initialized on modal click');
        }
      }).catch((error) => {
        console.warn('[OrderConfirmationModal] Failed to initialize audio on click:', error);
      });
    }
  };

  return (
    <Dialog
      open={orderConfirmationModal.visible}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      onClick={handleModalClick}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        p: 3,
        pb: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: '#000000',
        color: 'white',
        borderRadius: '12px 12px 0 0'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            New Order Received
          </Typography>
          <Box>
            {waitingMessage && (
              <Box
                sx={{
                  mt: 0,
                  ml: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 600,
                  width: 'fit-content',
                }}
              >
                {waitingMessage}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          Order #{displayData.id} • {formatTime(displayData.order_date)}
        </Typography>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
        <Card sx={{ borderRadius: 0, boxShadow: 'none' }}>
          <CardContent sx={{ p: 3 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading order details...
                </Typography>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Customer Info */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography fontWeight="bold" sx={{ pt: 0.5, pb: 0.5 }}>
                      {displayData.customer?.name}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: green[600],
                      cursor: displayData.customer?.phone && displayData.customer.phone !== "N/A" && profilePhoneNumber && displayData.order_code ? "pointer" : "default",
                      overflow: "hidden",
                      width: "fit-content",
                    }}
                    onClick={async () => {
                      const customerPhone = displayData.customer?.phone;
                      if (!customerPhone || customerPhone === "N/A" || !profilePhoneNumber || !displayData.order_code) {
                        notifyError("Phone number or order code not available");
                        return;
                      }

                      setCalling(true);
                      try {
                        const payload = {
                          order_id: displayData.order_code,
                          from: profilePhoneNumber,
                          to: customerPhone,
                        };
                        const response = await initiateExotelCall(payload);
                        if (response?.status === 200 || response?.status === 201) {
                          setShowCallInitiatedModal(true);
                        } else {
                          const errorMessage = response?.data?.message || "Failed to initiate call";
                          notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                        }
                      } catch (error: any) {
                        const errorMessage = error?.response?.data?.message || "Failed to initiate call";
                        notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                      } finally {
                        setCalling(false);
                      }
                    }}
                  >
                    <CallIcon
                      fontSize="small"
                      sx={{
                        mr: 0.5,
                        transition: "transform 0.3s",
                        opacity: calling ? 0.6 : 1,
                        cursor: displayData.customer?.phone && displayData.customer.phone !== "N/A" && profilePhoneNumber && displayData.order_code ? "pointer" : "default"
                      }}
                    />
                  </Box>
                </Box>

                {/* Address */}
                <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                  <LocationIcon color="action" fontSize="small" sx={{ mt: 0 }} />
                  <Typography variant="body2" color="text.secondary">
                    {displayData.shipping_address?.address3}, {displayData.shipping_address?.city}, {displayData.shipping_address?.state} - {displayData.shipping_address?.pincode}
                  </Typography>
                </Box>

                {/* Order Time */}
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <TimeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Placed {timeAgo}
                  </Typography>
                </Box>

                {/* Instruction - Only show if instruction exists */}
                {((displayData.instruction && displayData.instruction.trim() !== '') ||
                  ((displayData as any).instructions && (displayData as any).instructions.trim() !== '')) && (
                    <Box mb={3}>
                      <Typography fontSize={12} fontWeight={500} mb={0.5}>
                        Instruction:
                      </Typography>
                      <Typography fontSize={12} fontWeight={500} color="text.secondary">
                        {displayData.instruction || (displayData as any).instructions}
                      </Typography>
                    </Box>
                  )}

                <Divider sx={{ mb: 1 }} />

                {/* Order Items */}
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Order Items
                </Typography>
                <List dense>
                  {mainItems.map((item: any, index: number) => {
                    // Filter only add-ons (exclude the main product itself)
                    const addonItems = displayData.order_items.filter(
                      (e) => e.product_id.startsWith(`a-${item.product_id}`)
                    );

                    return (
                      <React.Fragment key={index}>
                        {/* Main Item */}
                        <ListItem sx={{ px: 0 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Box display="flex" alignItems="center" gap={1}>
                              {getProductTypeIcon(item.product_type)}
                              <Typography variant="body1" fontWeight="medium">
                                {item.product_name} × {item.quantity}
                              </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight="medium" color="text.primary">
                              ₹{item.total_price}
                            </Typography>
                          </Box>
                        </ListItem>

                        {/* Add-on Items */}
                        {addonItems.length > 0 &&
                          addonItems.map((e: any) => (
                            <ListItem key={e.product_id} sx={{ px: 2, opacity: 0.8 }}> {/* Indented for add-on */}
                              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                <Box display="flex" alignItems="center" gap={1}>
                                  {getProductTypeIcon(e.product_type)}
                                  <Typography variant="body2">
                                    {e.product_name} x {e.quantity}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} mr={-2}>
                                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                    ₹{e.total_price}
                                  </Typography>
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </List>


                <Divider sx={{ my: 2 }} />

                {/* Payment Summary */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Total <Chip
                      label={paymentStatusLabel}
                      size="small"
                      sx={{
                        fontWeight: 'medium',
                        bgcolor: isPaymentPaid ? 'green' : 'orange',
                        color: 'white',
                      }}
                    />
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mb={0}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setPaymentSummaryExpanded(!paymentSummaryExpanded)}
                  >
                    <Typography variant="h6" fontWeight="bold" color="black">
                      ₹{displayData.total_amount}
                    </Typography>
                    <IconButton size="small">
                      {paymentSummaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                </Box>

                {/* <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  mb={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setPaymentSummaryExpanded(!paymentSummaryExpanded)}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₹{displayData.total_amount}
                  </Typography>
                  <IconButton size="small">
                    {paymentSummaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box> */}

                {/* Collapsible Tax Breakdown */}
                <Collapse in={paymentSummaryExpanded}>
                  <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    {/* Sub Total */}
                    {/* <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Sub Total</Typography>
                      <Typography variant="body2">₹{displayData.sub_total}</Typography>
                    </Box> */}

                    {/* Shipping Charge */}
                    {/* {parseFloat(displayData.shipping_charge || '0') > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Delivery Charge</Typography>
                        <Typography variant="body2">₹{displayData.shipping_charge}</Typography>
                      </Box>
                    )} */}

                    {/* Tax Breakdown */}
                    {parseFloat(displayData.sgst || '0') > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">SGST</Typography>
                        <Typography variant="body2">₹{displayData.sgst}</Typography>
                      </Box>
                    )}

                    {parseFloat(displayData.cgst || '0') > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">CGST</Typography>
                        <Typography variant="body2">₹{displayData.cgst}</Typography>
                      </Box>
                    )}

                    {parseFloat(displayData.igst || '0') > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">IGST</Typography>
                        <Typography variant="body2">₹{displayData.igst}</Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Grand Total */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight="bold" color="black">
                        Grand Total
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="black">
                        ₹{displayData.total_amount}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>

                {/* Cancellation reason selection moved to CancelOrderModal */}
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        p: 3,
        gap: 2,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'grey.50',
        borderRadius: '0 0 12px 12px'
      }}>
        <Button
          onClick={handleReject}
          color="error"
          startIcon={processing ? <CircularProgress size={20} /> : <RejectIcon />}
          variant="outlined"
          disabled={processing || loading}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 'medium',
            minWidth: 140
          }}
        >
          Cancel Order
        </Button>
        <Button
          onClick={handleAccept}
          color="success"
          startIcon={processing ? <CircularProgress size={20} /> : <AcceptIcon />}
          variant="contained"
          disabled={processing || loading}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 'medium',
            minWidth: 140
          }}
        >
          Accept Order
        </Button>
      </DialogActions>

      {/* Accept Order Modal */}
      <AcceptOrderModal
        open={showAcceptModal}
        onClose={() => setShowAcceptModal(false)}
        orderData={displayData}
        onAcceptSuccess={handleAcceptSuccess}
      />

      {/* Cancel Order Modal */}
      <CancelOrderModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderData={displayData}
        onCancelSuccess={handleCancelSuccess}
      />

      {/* Call Initiated Modal */}
      <CallInitiatedModal
        open={showCallInitiatedModal}
        onClose={() => setShowCallInitiatedModal(false)}
        phoneNumber={profilePhoneNumber}
      />
    </Dialog>
  );
};

export default OrderConfirmationModal;


